import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';

const DOC_TITLES = {
  'privacy-policy': {
    fr: 'Politique de confidentialité',
    de: 'Datenschutzerklärung',
  },
  'terms': {
    fr: "Conditions Générales d'Utilisation",
    de: 'Allgemeine Geschäftsbedingungen',
  },
  'legal-notice': {
    fr: 'Mentions légales',
    de: 'Impressum',
  },
};

const LegalDocPage = ({ docKey }) => {
  const { language, t, toggleLanguage } = useLanguage();
  const { isDark } = useTheme();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const docTitle = DOC_TITLES[docKey]?.[language] || 'CarbonScope';
    document.title = `${docTitle} — CarbonScope`;
  }, [docKey, language]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/legal-docs/${docKey}_${language}.md`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load legal doc:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [docKey, language]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">{t('common.loading') || 'Loading...'}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8">
        <h1 className="text-2xl font-bold mb-4">
          {t('legal.docUnavailable') || 'Document indisponible'}
        </h1>
        <Link to="/auth" className="text-blue-600 hover:underline">
          {t('legal.backToHome') || 'Retour à la page de connexion'}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <Link
            to="/auth"
            className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
          >
            ← {t('legal.backToHome') || 'Retour'}
          </Link>
          <div className="flex gap-2 text-sm" aria-label="Language">
            <button
              onClick={() => language !== 'fr' && toggleLanguage()}
              className={`px-2 py-1 rounded ${
                language === 'fr'
                  ? 'font-bold text-blue-600 dark:text-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              aria-label={t('common.languages.fr')}
              aria-pressed={language === 'fr'}
            >
              FR
            </button>
            <span className="text-gray-400" aria-hidden="true">|</span>
            <button
              onClick={() => language !== 'de' && toggleLanguage()}
              className={`px-2 py-1 rounded ${
                language === 'de'
                  ? 'font-bold text-blue-600 dark:text-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              aria-label={t('common.languages.de')}
              aria-pressed={language === 'de'}
            >
              DE
            </button>
          </div>
        </div>
        <article
          className={`prose ${isDark ? 'prose-invert' : ''} prose-slate max-w-none
            prose-headings:font-bold
            prose-h1:text-3xl
            prose-h2:text-2xl prose-h2:mt-8
            prose-h3:text-xl
            prose-a:text-blue-600 dark:prose-a:text-blue-400
            prose-table:my-4
            prose-th:bg-gray-100 dark:prose-th:bg-gray-800
            prose-td:p-2
          `}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => {
                const isExternal = props.href?.startsWith('http') || props.href?.startsWith('mailto:');
                if (isExternal) {
                  return <a {...props} target="_blank" rel="noopener noreferrer" />;
                }
                return <a {...props} />;
              },
              // RESP-2 défaut 1 — les tables GFM (privacy-policy fr/de)
              // débordaient la page à 390px, ce que la CIBLE VISUELLE
              // interdit. Approche A : le conteneur scrolle, pas la page.
              // Purement structurel (un div de plus dans le DOM), aucune
              // règle CSS >= lg ajoutée : overflow-x-auto est déjà au bundle.
              // Sur desktop, une table qui tient dans sa largeur ne déclenche
              // aucun scroll — le rendu reste identique.
              table: ({ node, ...props }) => (
                <div className="overflow-x-auto">
                  <table {...props} />
                </div>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
};

export default LegalDocPage;
